import { Head, router } from "@inertiajs/react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { Bell, Check, CheckCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import type { BreadcrumbItem, SerializedNotification } from "@/types";

interface NotificationsIndexProps {
  notifications: SerializedNotification[];
  unread_count: number;
}

export default function NotificationsIndex({
  notifications,
  unread_count,
}: NotificationsIndexProps) {
  const routes = useOrganizationRoutes();

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Notificaciones",
      href: routes.notifications.index(),
    },
  ];

  const handleMarkAsRead = (id: number) => {
    router.patch(
      routes.notifications.markAsRead(id),
      {},
      { preserveScroll: true },
    );
  };

  const handleMarkAllAsRead = () => {
    router.patch(
      routes.notifications.markAllAsRead(),
      {},
      { preserveScroll: true },
    );
  };

  const handleNotificationClick = (notification: SerializedNotification) => {
    if (notification.url) {
      router.visit(notification.url);
    }
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Notificaciones" />
      <div className="flex h-full flex-1 flex-col gap-4 p-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col space-y-2">
            <h2 className="text-2xl font-bold">Notificaciones</h2>
            <p className="text-muted-foreground">
              {unread_count > 0
                ? `Tenés ${unread_count} notificaciones sin leer`
                : "No hay notificaciones sin leer"}
            </p>
          </div>
          {unread_count > 0 && (
            <Button variant="outline" onClick={handleMarkAllAsRead}>
              <CheckCheck className="mr-2 h-4 w-4" />
              Marcar todas como leídas
            </Button>
          )}
        </div>

        <div className="space-y-2">
          {notifications.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Bell className="h-12 w-12 text-muted-foreground/50" />
                <p className="mt-4 text-muted-foreground">
                  No hay notificaciones
                </p>
              </CardContent>
            </Card>
          ) : (
            notifications.map((notification) => (
              <Card
                key={notification.id}
                className={`cursor-pointer transition-colors hover:bg-muted/50 ${
                  !notification.read_at ? "border-l-4 border-l-primary" : ""
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <CardHeader className="py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <CardTitle
                        className={`text-base ${
                          !notification.read_at
                            ? "font-semibold"
                            : "font-normal text-muted-foreground"
                        }`}
                      >
                        {notification.title}
                      </CardTitle>
                      <p
                        className={`mt-1 text-sm ${
                          !notification.read_at
                            ? "text-muted-foreground"
                            : "text-muted-foreground/70"
                        }`}
                      >
                        {notification.body}
                      </p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {formatDistanceToNow(
                          new Date(notification.created_at),
                          {
                            addSuffix: true,
                            locale: es,
                          },
                        )}
                      </p>
                    </div>
                    {!notification.read_at && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id);
                        }}
                      >
                        <Check className="h-4 w-4" />
                        <span className="sr-only">Marcar como leída</span>
                      </Button>
                    )}
                  </div>
                </CardHeader>
              </Card>
            ))
          )}
        </div>
      </div>
    </AppLayout>
  );
}
